import type { Choice, Results } from "./types";

/**
 * ═══ LES NOMBRES DU TABLEAU DE RÉSULTATS ═══
 *
 * Sans React, pour que la mise en page du mur (`roomLayout.ts`) compte les lignes
 * dans l'ordre même où `Tally` les affiche.
 */

/** La part d'une réponse, qu'on ait reçu les voix ou seulement les parts. */
export function shareOf(
  row: Results["perChoice"][number],
  total: number,
): number {
  if (typeof row.share === "number") return row.share;
  if (!total) return 0;
  return (row.votes ?? 0) / total;
}

/**
 * LES LIGNES DU TABLEAU, LA PLUS CHOISIE EN TÊTE (tâche #337).
 *
 * ⚠️ RANGÉES PAR PART, PAS PAR VOIX : quand les chiffres sont masqués, le serveur
 * n'envoie que les parts (voir `Results.tsx`), et le classement doit rester le même
 * qu'on les montre ou non. À part égale, l'ordre de l'organisateur.
 *
 * ⚠️ `index` EST LA PLACE DANS L'ACTIVITÉ, PAS LE RANG. C'est lui qui donne le numéro
 * du vote par SMS (`smsVote.ts`) : trier ne doit pas renuméroter les réponses sous
 * les yeux de ceux qui viennent d'envoyer « 2 ».
 */
export function tallyRows<C extends Pick<Choice, "id">>(
  choices: readonly C[],
  results: Results,
): { choice: C; index: number; votes?: number; share: number }[] {
  const total = results.totalVotes ?? 0;
  return choices
    .map((choice, index) => {
      const row = results.perChoice.find((r) => r.choiceId === choice.id);
      return {
        choice,
        index,
        votes: row?.votes,
        share: row ? shareOf(row, total) : 0,
      };
    })
    .sort((a, b) => b.share - a.share || a.index - b.index);
}

/** Deux décimales séparent deux réponses à une voix d'écart jusqu'à dix mille voix. */
const MAX_DECIMALS = 2;

/**
 * LES POURCENTAGES ÉCRITS, DÉPARTAGÉS QUAND L'ARRONDI LES CONFOND (tâche #338).
 *
 * Arrondies à l'unité, deux réponses peuvent afficher le même « 33% » sans avoir
 * autant de voix : 334 contre 333 sur mille. Avec le nombre de votants, « (334) » et
 * « (333) » les départagent ; quand l'organisateur l'a masqué (`untie`), il ne reste
 * que la part, et le tableau en met une en tête sans que rien à l'écran dise
 * pourquoi. Ces lignes-là, et elles seules, prennent alors une décimale — deux s'il
 * le faut — jusqu'à ce que l'écart se lise : « 33,4% » et « 33,3% ».
 *
 * ⚠️ UNE VRAIE ÉGALITÉ RESTE À L'UNITÉ : deux parts identiques n'ont pas d'écart à
 * montrer. Et les lignes départagées prennent toutes le même nombre de décimales,
 * pour se comparer d'un coup d'œil.
 *
 * Dans la langue du lecteur, par `Intl` : « 33,4% » en français, « 33.4% » en anglais.
 */
export function percentLabels(
  shares: readonly number[],
  { untie, locale }: { untie: boolean; locale: string },
): string[] {
  const formats = new Map<number, Intl.NumberFormat>();
  const write = (share: number, decimals: number) => {
    let format = formats.get(decimals);
    if (!format) {
      const options = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
      try {
        format = new Intl.NumberFormat(locale, options);
      } catch {
        format = new Intl.NumberFormat(undefined, options);
      }
      formats.set(decimals, format);
    }
    return `${format.format(share * 100)}%`;
  };
  /** Deux parts différentes sous le même libellé, parmi `indexes`. */
  const confused = (labels: string[], indexes: number[]) => (i: number) =>
    indexes.some((j) => shares[j] !== shares[i] && labels[j] === labels[i]);

  const whole = shares.map((share) => write(share, 0));
  if (!untie) return whole;
  const everyone = shares.map((_, i) => i);
  const tied = everyone.filter(confused(whole, everyone));
  if (!tied.length) return whole;

  let decimals = 1;
  let labels = shares.map((share) => write(share, decimals));
  while (decimals < MAX_DECIMALS && tied.some(confused(labels, tied))) {
    decimals += 1;
    labels = shares.map((share) => write(share, decimals));
  }
  return labels.map((label, i) => (tied.includes(i) ? label : whole[i] ?? label));
}
